import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useChildOrgsOrdered } from '@/api/hooks/useActivityLog';
import { activityLogService } from '@/api/services/activity-log.service';
import { PRIVILEGES } from '@/api/types/dtos';
import { useHasPrivilege } from '@/components/authorization';
import { useReportOrgSelection } from '@/hooks/useReportOrgSelection';
import { useFetchAllOrgIds } from '@/hooks/useFetchAllOrgIds';
import ReportRequestDialog from '@/components/reports/ReportRequestDialog';
import FarmDetailsModal from '@/components/modal/FarmDetailsModal';
import type { ChildOrgWithActivityDto, HealthSummaryDto } from '@/api/types/dtos/activity-log/activity-log.dto';

interface Props {
  summary: HealthSummaryDto | null;
}

const ORG_FILTER_TITLES: Record<string, string> = {
  active: 'Organizaciones Activas',
  inactive: 'Organizaciones Inactivas',
  updated7d: 'Actualizadas en los últimos 7 días',
  updated30d: 'Actualizadas en los últimos 30 días',
  updated90d: 'Actualizadas en los últimos 90 días',
  older90d: 'Sin actividad en más de 90 días',
  trainingsActive: 'Con Actividad de Capacitación',
  inspectionsCompleted: 'Con Actividad de Retos',
};

const MetricCard: React.FC<{
  label: string;
  value: number | string;
  subtitle?: string;
  color?: string;
  onClick?: () => void;
}> = ({ label, value, subtitle, color = 'primary.main', onClick }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
      transition: 'background-color 0.15s',
    }}
    onClick={onClick}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" fontWeight={700} color={color}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18 }}>
      {subtitle ?? ''}
    </Typography>
  </Paper>
);

const OrgListDialog: React.FC<{
  open: boolean;
  filterKey: string | null;
  onClose: () => void;
}> = ({ open, filterKey, onClose }) => {
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<ChildOrgWithActivityDto[]>([]);
  const pageSize = 50;

  const { data, isLoading } = useChildOrgsOrdered(
    open && filterKey
      ? { sortBy: 'updatedAt', activityFilter: filterKey, page, size: pageSize }
      : undefined
  );

  // Report-from-anywhere state (only for users with CREATE_REPORT)
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const selection = useReportOrgSelection();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Fetch every org matching this dialog's filter (across pages)
  const { fetchAll: fetchAllOrgIds, isLoading: selectingAll, progress: selectAllProgress } =
    useFetchAllOrgIds(
      React.useCallback(
        async (page: number, size: number) => {
          if (!filterKey) return { items: [], totalElements: 0, totalPages: 0 };
          const res = await activityLogService.getChildOrgsOrdered({
            sortBy: 'updatedAt',
            activityFilter: filterKey,
            page,
            size,
          });
          const content = res.data?.content ?? [];
          return {
            items: content.map((org) => ({ id: org.id, name: org.name })),
            totalElements: res.data?.totalElements ?? 0,
            totalPages: res.data?.totalPages ?? 0,
          };
        },
        [filterKey]
      )
    );

  const handleSelectAllMatching = useCallback(async () => {
    const orgs = await fetchAllOrgIds();
    if (orgs.length > 0) {
      selection.selectMultiple(orgs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllOrgIds, selection.selectMultiple]);

  const totalCount = data?.totalElements ?? 0;
  const isLastPage = data?.isLast ?? true;

  // Accumulate results as pages load
  useEffect(() => {
    if (data?.content) {
      setAccumulated((prev) => (page === 0 ? data.content : [...prev, ...data.content]));
    }
  }, [data?.content, page]);

  // Reset when filter changes
  useEffect(() => {
    setPage(0);
    setAccumulated([]);
  }, [filterKey]);

  // Track prev open state to reset on re-open
  const clearSelection = selection.clearSelection;
  const prevOpen = useRef(open);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPage(0);
      setAccumulated([]);
      clearSelection();
    }
    prevOpen.current = open;
  }, [open, clearSelection]);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);

  const handleOrgClick = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    setFarmDetailsOpen(true);
  }, []);

  const handleFarmDetailsClose = useCallback(() => {
    setFarmDetailsOpen(false);
    setSelectedOrgId(null);
  }, []);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {filterKey ? ORG_FILTER_TITLES[filterKey] : ''}
            </Typography>
            <Button onClick={onClose} startIcon={<CloseIcon />}>
              Cerrar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoading && accumulated.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!(isLoading && accumulated.length === 0) && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {totalCount} organización{totalCount !== 1 ? 'es' : ''} encontrada{totalCount !== 1 ? 's' : ''}
              </Typography>
              {totalCount === 0 && !isLoading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No se encontraron organizaciones con este criterio</Typography>
                </Box>
              ) : (
                <>
                  {canCreateReport && (
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        mx: -3,
                        px: 3,
                        py: 1,
                        mb: 2,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          selection.selectMultiple(
                            accumulated.map((org) => ({ id: org.id, name: org.name }))
                          )
                        }
                        disabled={selectingAll}
                      >
                        Seleccionar cargadas
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleSelectAllMatching}
                        disabled={selectingAll}
                        startIcon={selectingAll ? <CircularProgress size={16} /> : undefined}
                      >
                        {selectingAll
                          ? `${selectAllProgress?.loaded ?? 0}/${selectAllProgress?.total || '?'}`
                          : 'Seleccionar todo el filtro'}
                      </Button>
                      {selection.selectedCount > 0 && (
                        <Button
                          variant="text"
                          size="small"
                          color="inherit"
                          onClick={selection.clearSelection}
                        >
                          Limpiar
                        </Button>
                      )}
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        {selection.selectedCount} seleccionada
                        {selection.selectedCount === 1 ? '' : 's'}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setReportDialogOpen(true)}
                        disabled={selection.selectedCount === 0}
                      >
                        Descargar Reporte
                      </Button>
                    </Box>
                  )}
                  <List>
                    {accumulated.map((org) => (
                      <ListItem
                        key={org.id}
                        disablePadding
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <ListItemButton onClick={() => handleOrgClick(org.id)} sx={{ borderRadius: 1 }}>
                          {canCreateReport && (
                            <ListItemIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                selection.toggleSelection({ id: org.id, name: org.name });
                              }}
                            >
                              <Checkbox
                                edge="start"
                                checked={selection.isSelected(org.id)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-label': `Seleccionar ${org.name}` }}
                              />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={org.name}
                            secondary={
                              org.lastActivityOverall
                                ? `Última actividad: ${new Date(org.lastActivityOverall).toLocaleDateString()}`
                                : 'Sin actividad registrada'
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  {!isLastPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setPage((p) => p + 1)}
                        disabled={isLoading}
                      >
                        {isLoading ? <CircularProgress size={24} /> : 'Cargar Más'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedOrgId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedOrgId}
        />
      )}

      {canCreateReport && (
        <ReportRequestDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          orgs={selection.selectedOrgs}
          sourceLabel={filterKey ? ORG_FILTER_TITLES[filterKey] : undefined}
        />
      )}
    </>
  );
};

const HealthSummaryCards: React.FC<Props> = ({ summary }) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleCardClick = (filterKey: string) => {
    setActiveFilter(filterKey);
  };

  if (!summary) {
    return (
      <Typography color="text.secondary">
        No hay datos de salud disponibles para esta organización.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Salud de la Organización
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total Organizaciones"
            value={summary.totalChildOrgs}
            color="text.primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Activas"
            value={summary.activeOrgs}
            subtitle={`${((summary.activeOrgs / Math.max(summary.totalChildOrgs, 1)) * 100).toFixed(1)}%`}
            color="success.main"
            onClick={() => handleCardClick('active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Inactivas"
            value={summary.inactiveOrgs}
            color="error.main"
            onClick={() => handleCardClick('inactive')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Eventos (7d)"
            value={summary.recentEventCount7d}
            color="info.main"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Actualización de Actividad
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Última semana" value={summary.orgsUpdatedLast7d} color="success.main" onClick={() => handleCardClick('updated7d')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Último mes" value={summary.orgsUpdatedLast30d} color="info.main" onClick={() => handleCardClick('updated30d')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Últimos 3 meses" value={summary.orgsUpdatedLast90d} color="warning.main" onClick={() => handleCardClick('updated90d')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Más de 3 meses" value={summary.orgsOlderThan90d} color="error.main" onClick={() => handleCardClick('older90d')} />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Programas
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Capacitaciones Activas" value={summary.trainingsActive} color="info.main" onClick={() => handleCardClick('trainingsActive')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Capacitaciones Completadas" value={summary.trainingsCompletedCount} color="success.main" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Retos Abiertos" value={summary.inspectionsOpen} color="warning.main" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Retos Completados" value={summary.inspectionsCompletedCount} color="success.main" onClick={() => handleCardClick('inspectionsCompleted')} />
        </Grid>
      </Grid>

      {/* Org List Dialog */}
      <OrgListDialog
        open={!!activeFilter}
        filterKey={activeFilter}
        onClose={() => setActiveFilter(null)}
      />
    </Box>
  );
};

export default HealthSummaryCards;
